import type { DuaWorkspaceDatabase } from "./database";
import {
  APP_METADATA_ID,
  AppMetadataSchema,
  AppSettingsSchema,
  BACKUP_METADATA_ID,
  BackupMetadataSchema,
  CollectionSchema,
  createDefaultAppMetadata,
  createDefaultBackupMetadata,
  createDefaultSettings,
  detectSystemTimeZone,
  SETTINGS_ID,
  type Collection,
} from "../domain";
import { normalizeLabel } from "../domain";

export const BOOTSTRAP_VERSION = 1;
export const PARENTS_COLLECTION_ID = "10000000-0000-4000-8000-000000000001";

export const DEFAULT_COLLECTIONS = [
  { id: PARENTS_COLLECTION_ID, name: "Parents", icon: "heart-handshake", accent: "#7d9274" },
  { id: "10000000-0000-4000-8000-000000000002", name: "Family", icon: "users", accent: "#a6806c" },
  { id: "10000000-0000-4000-8000-000000000003", name: "Akhirah", icon: "sparkles", accent: "#6f887f" },
  { id: "10000000-0000-4000-8000-000000000004", name: "Forgiveness", icon: "refresh-cw", accent: "#9b8462" },
  { id: "10000000-0000-4000-8000-000000000005", name: "Guidance", icon: "compass", accent: "#738d83" },
  { id: "10000000-0000-4000-8000-000000000006", name: "Health", icon: "heart-pulse", accent: "#8d796e" },
  { id: "10000000-0000-4000-8000-000000000007", name: "Rizq", icon: "sprout", accent: "#718e70" },
  { id: "10000000-0000-4000-8000-000000000008", name: "Marriage", icon: "rings", accent: "#a17d83" },
  { id: "10000000-0000-4000-8000-000000000009", name: "Character", icon: "gem", accent: "#7e8393" },
  { id: "10000000-0000-4000-8000-000000000010", name: "Friends", icon: "user-round", accent: "#8b866c" },
  { id: "10000000-0000-4000-8000-000000000011", name: "Ummah", icon: "globe-2", accent: "#66877c" },
  { id: "10000000-0000-4000-8000-000000000012", name: "Du'a Requests", icon: "message-circle-heart", accent: "#987868" },
] as const;

export type DefaultCollectionName = (typeof DEFAULT_COLLECTIONS)[number]["name"];

export interface BootstrapOptions {
  collectionNames?: readonly DefaultCollectionName[];
  now?: string;
  timezone?: string;
}

export async function bootstrapWorkspace(
  database: DuaWorkspaceDatabase,
  options: BootstrapOptions = {},
): Promise<void> {
  const timestamp = options.now ?? new Date().toISOString();
  const selectedNames = new Set(
    options.collectionNames ?? DEFAULT_COLLECTIONS.map((collection) => collection.name),
  );

  await database.transaction(
    "rw",
    [database.collections, database.settings, database.appMetadata, database.backupMetadata],
    async () => {
      let metadata = await database.appMetadata.get(APP_METADATA_ID);
      if (metadata === undefined) {
        metadata = createDefaultAppMetadata(timestamp);
        await database.appMetadata.add(metadata);
      }

      if ((await database.settings.get(SETTINGS_ID)) === undefined) {
        await database.settings.add(createDefaultSettings(timestamp, options.timezone));
      }

      if ((await database.backupMetadata.get(BACKUP_METADATA_ID)) === undefined) {
        await database.backupMetadata.add(createDefaultBackupMetadata(timestamp));
      }

      // An explicit collection selection may arrive after the database itself is
      // initialized (the first-run wizard needs IndexedDB before it is finished).
      // Re-applying an explicit selection is idempotent; implicit defaults remain
      // a one-time bootstrap so user-deleted defaults are never recreated later.
      if (
        metadata.bootstrapVersion < BOOTSTRAP_VERSION ||
        options.collectionNames !== undefined
      ) {
        const existingNames = new Set(
          (await database.collections.toArray()).map((collection) => collection.normalizedName),
        );
        const collections: Collection[] = DEFAULT_COLLECTIONS.filter(
          (collection) =>
            selectedNames.has(collection.name) && !existingNames.has(normalizeLabel(collection.name)),
        ).map((collection, index) =>
          CollectionSchema.parse({
            ...collection,
            normalizedName: normalizeLabel(collection.name),
            sortOrder: index,
            createdAt: timestamp,
            updatedAt: timestamp,
          }),
        );
        if (collections.length > 0) {
          await database.collections.bulkAdd(collections);
        }
      }

      await database.appMetadata.put(
        AppMetadataSchema.parse({
          ...metadata,
          databaseVersion: database.verno,
          bootstrapVersion: Math.max(metadata.bootstrapVersion, BOOTSTRAP_VERSION),
          lastOpenedAt: timestamp,
          updatedAt: timestamp,
        }),
      );

      const settings = await database.settings.get(SETTINGS_ID);
      if (settings !== undefined) {
        const detectedTimezone = options.timezone ?? detectSystemTimeZone();
        const refreshedSettings = AppSettingsSchema.parse({
          ...settings,
          timezone: { ...settings.timezone, detected: detectedTimezone },
          updatedAt:
            settings.timezone.detected === detectedTimezone ? settings.updatedAt : timestamp,
        });
        if (refreshedSettings.timezone.detected !== settings.timezone.detected) {
          await database.settings.put(refreshedSettings);
        }
      }
      const backupMetadata = await database.backupMetadata.get(BACKUP_METADATA_ID);
      if (backupMetadata !== undefined) {
        BackupMetadataSchema.parse(backupMetadata);
      }
    },
  );
}

export async function initializeDatabase(
  database: DuaWorkspaceDatabase,
  options: BootstrapOptions = {},
): Promise<DuaWorkspaceDatabase> {
  if (!database.isOpen()) {
    await database.open();
  }
  await bootstrapWorkspace(database, options);
  return database;
}
